import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface ConfirmationDialogProps {
  open: boolean;
  title?: string;
  message: string;
  ruleName?: string;
  confirmText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  title = 'Confirm Approval',
  message,
  ruleName,
  confirmText = 'Confirm',
  onConfirm,
  onCancel
}) => {

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        onCancel();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          backgroundColor: '#fff'
        }
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      {/* Header with close button only - NO ICON, NO BORDER */}
      <DialogTitle 
        sx={{ 
          pb: 2,
          pt: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#fff'
        }}
      >
        <Typography 
          component="span"
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            color: '#000',
            fontSize: '20px',
            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
          }}
        >
          {title}
        </Typography>
        <IconButton 
          onClick={onCancel} 
          size="small"
          sx={{
            color: '#666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Message content with bold rule name */}
      <DialogContent sx={{ pb: 1, px: 3 }}>
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#555',
            fontSize: 15,
            lineHeight: 1.6,
            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
          }}
        >
          {message} <strong>{ruleName}</strong>
        </Typography>
      </DialogContent>

      {/* Single Confirm button - NO BORDER, WHITE BACKGROUND */}
      <DialogActions 
        sx={{ 
          p: 2,
          pt: 3,
          gap: 1,
          backgroundColor: '#fff',
          justifyContent: 'flex-end'
        }}
      >
        <Button
          variant="contained"
          onClick={onConfirm}
          sx={{
            backgroundColor: '#2196F3',
            color: '#fff',
            fontWeight: 600,
            px: 4,
            py: 1,
            textTransform: 'none',
            fontSize: 16,
            borderRadius: '8px',
            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
            '&:hover': {
              backgroundColor: '#1976D2'
            }
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
